import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/api/engine';
import type { NotificationPreferences } from '@/api/types';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@/components/settings/notificationPreferencesDefaults';
import {
  DropdownSetting,
  PanelShell,
  PanelSkeleton,
  SettingsSection,
  SliderSetting,
  ToggleSetting,
  normalizeError,
  type NormalizedError,
  type SaveStatus,
} from '@seitiate/settings-ui';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${hour.toString().padStart(2, '0')}:00`,
}));

function formatTypeLabel(value: string): string {
  return value
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * NotificationSettingsPanel — proof-of-concept consumer for the shared shell.
 *
 * Pre-refactor this panel hand-rolled its own loading, error, and save-state
 * surfaces with ad-hoc Tailwind classes. It now routes all of those through
 * PanelShell + SaveStatusChip + RetryableErrorBanner, and pipes API errors
 * through normalizeError() so the body dict-shape (commission 2) and the
 * plain-string shape (older engine paths) both surface correctly.
 */
export const NotificationSettingsPanel: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<NormalizedError | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: 'idle' });

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.notifications.getPreferences();
      setPreferences({ ...DEFAULT_NOTIFICATION_PREFERENCES, ...response });
    } catch (err) {
      setLoadError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPreferences();
  }, [loadPreferences]);

  const enabledTypes = useMemo(
    () => Object.entries(preferences.enabled_types || {}).sort(([left], [right]) => left.localeCompare(right)),
    [preferences.enabled_types]
  );

  // UX State Doctrine §3.4: autosaves do NOT optimistically update UI on change.
  // We issue the write, flip the chip to 'saving', and only reconcile local
  // state from the backend response. Intermediate control clicks don't visually
  // "stick" until the server confirms — this is deliberate, prevents divergence,
  // and is the ratified behavior for every panel.
  const savePreferences = useCallback(async (next: NotificationPreferences) => {
    setSaveStatus({ state: 'saving' });
    try {
      const response = await api.notifications.putPreferences(next);
      setPreferences({ ...DEFAULT_NOTIFICATION_PREFERENCES, ...response });
      setSaveStatus({ state: 'saved', at: Date.now() });
    } catch (err) {
      const normalized = normalizeError(err);
      setSaveStatus({
        state: 'failed',
        message: normalized.message,
        onRetry: () => void savePreferences(next),
      });
    }
  }, []);

  const updateField = <K extends keyof NotificationPreferences>(key: K, value: NotificationPreferences[K]) => {
    void savePreferences({ ...preferences, [key]: value });
  };

  if (loading) {
    return (
      <PanelShell title="Notifications" description="Delivery controls.">
        <PanelSkeleton rows={4} />
      </PanelShell>
    );
  }

  if (loadError) {
    return (
      <PanelShell
        title="Notifications"
        description="Delivery controls."
        error={loadError}
        onRetryError={() => void loadPreferences()}
      >
        {/* body empty on load error — the footer banner handles the surface */}
        <div />
      </PanelShell>
    );
  }

  return (
    <PanelShell
      title="Notifications"
      description="Where to route alerts, when to stay quiet, and how often."
      saveStatus={saveStatus}
      showRefresh
      onRefresh={() => void loadPreferences()}
    >
      <SettingsSection title="Delivery Channels" anchorId="section-notifications-delivery">
        <ToggleSetting
          anchorId="notifications-web_enabled"
          label="Web notifications"
          checked={preferences.web_enabled}
          onChange={(value) => updateField('web_enabled', value)}
          description="Show in-app notifications in Sakme."
        />
        <ToggleSetting
          anchorId="notifications-push_enabled"
          label="Push notifications"
          checked={preferences.push_enabled}
          onChange={(value) => updateField('push_enabled', value)}
          description="Send mobile push alerts when device registration is active."
        />
        <ToggleSetting
          anchorId="notifications-telegram_enabled"
          label="Telegram notifications"
          checked={preferences.telegram_enabled}
          onChange={(value) => updateField('telegram_enabled', value)}
          description="Route eligible alerts to the Telegram bridge when connected."
        />
      </SettingsSection>

      <SettingsSection title="Rate Limits" anchorId="section-notifications-rate-limits">
        <SliderSetting
          anchorId="notifications-max_per_type_per_day"
          label="Daily limit per notification type"
          value={preferences.max_per_type_per_day}
          min={1}
          max={200}
          onChange={(value) => updateField('max_per_type_per_day', value)}
          description="Hard cap to keep one alert type from spamming you (1–200 per day)."
        />
      </SettingsSection>

      <SettingsSection
        title="Quiet Hours"
        defaultExpanded={false}
        anchorId="section-notifications-quiet-hours"
      >
        <DropdownSetting
          anchorId="notifications-quiet_hours_start"
          label="Quiet hours start"
          value={preferences.quiet_hours_start === null ? '' : String(preferences.quiet_hours_start)}
          options={HOUR_OPTIONS}
          onChange={(value) => updateField('quiet_hours_start', value === '' ? null : Number(value))}
          placeholder="Off"
          description="Suppress non-urgent notifications after this hour."
        />
        <DropdownSetting
          anchorId="notifications-quiet_hours_end"
          label="Quiet hours end"
          value={preferences.quiet_hours_end === null ? '' : String(preferences.quiet_hours_end)}
          options={HOUR_OPTIONS}
          onChange={(value) => updateField('quiet_hours_end', value === '' ? null : Number(value))}
          placeholder="Off"
          description="Resume delivery at this hour."
        />
      </SettingsSection>

      <SettingsSection
        title="Per-Type Controls"
        defaultExpanded={enabledTypes.length > 0}
        badge={enabledTypes.length > 0 ? `${enabledTypes.length} live` : 'AUTO'}
        badgeColor={enabledTypes.length > 0 ? 'var(--sp-accent)' : 'var(--sp-text-muted)'}
        anchorId="section-notifications-per-type"
      >
        {enabledTypes.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {enabledTypes.map(([type, enabled]) => (
              <ToggleSetting
                key={type}
                anchorId={`notifications-enabled_types-${type}`}
                label={formatTypeLabel(type)}
                checked={enabled}
                onChange={(value) =>
                  updateField('enabled_types', {
                    ...preferences.enabled_types,
                    [type]: value,
                  })
                }
                description={`Allow ${formatTypeLabel(type).toLowerCase()} alerts to reach you.`}
              />
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 10, color: 'var(--sp-text-muted)' }}>
            No backend-registered notification types have been written into this tenant yet. Global delivery controls above are
            live now.
          </p>
        )}
      </SettingsSection>
    </PanelShell>
  );
};
